/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * MW ADVANCED PRODUCT OPTIONS (APO) — SOLD OUT BUTTON SYNC
 * ------------------------------------------------------------------------------------------------------------
 *
 * A customization in theme.js disables the theme's normal buy-button re-render whenever the MW Advanced
 * Product Options app is on the page (it guards the swap with `!document.querySelector('.mw-apo-container')`
 * so APO's cloned add-to-cart button keeps working). The side effect is that the Add to cart / Sold out
 * button no longer updates when switching between an available and a sold-out variant — the inventory block
 * updates, but the button keeps its initial state.
 *
 * This handler restores correct button state WITHOUT replacing the button node (so APO's bindings survive).
 * On every variant change the theme fetches freshly-rendered section HTML and dispatches `product:rerender`
 * (a non-bubbling event on the form, so we listen in the capture phase). We read the correctly-rendered
 * button out of that HTML and copy its label, disabled state and classes onto the live button in place, and
 * toggle the dynamic checkout ("Buy with Shop") button to match.
 */
(function () {
  // Mirror theme.js's deepQuerySelector: section responses may wrap content inside <template> elements.
  function deepQuerySelector(root, selector) {
    let element = root.querySelector(selector);
    if (element) {
      return element;
    }
    for (const template of root.querySelectorAll('template')) {
      element = deepQuerySelector(template.content, selector);
      if (element) {
        return element;
      }
    }
    return null;
  }

  document.addEventListener(
    'product:rerender',
    function (event) {
      // Only needed when APO is present — otherwise the theme already swaps the buttons itself.
      if (!document.querySelector('.mw-apo-container')) {
        return;
      }

      const fragment = event.detail && event.detail.htmlFragment;
      const form = event.target;
      if (!fragment || !form || typeof form.getAttribute !== 'function') {
        return;
      }

      const formId = form.getAttribute('id');

      // Buttons that belong to this form: either nested inside it (#AddToCart) or linked via form="" (#StickyAddToCart).
      const liveButtons = Array.from(document.querySelectorAll('[data-product-add-to-cart-button]')).filter(
        (button) => button.closest('form') === form || button.getAttribute('form') === formId
      );

      liveButtons.forEach(function (button) {
        if (!button.id) {
          return;
        }

        const newButton = deepQuerySelector(fragment, '#' + (window.CSS && CSS.escape ? CSS.escape(button.id) : button.id));
        if (!newButton) {
          return;
        }

        // Disabled state + availability classes (button--ternary / button--primary / button--secondary).
        button.disabled = newButton.hasAttribute('disabled');
        button.className = newButton.className;

        if (newButton.hasAttribute('data-button-content')) {
          button.setAttribute('data-button-content', newButton.getAttribute('data-button-content'));
        }

        // Update only the visible label so loader-button's internal spinner markup is preserved.
        const newLabel = newButton.textContent.trim();
        const textElement = button.querySelector('.loader-button__text');
        if (textElement) {
          textElement.textContent = newLabel;
        } else {
          button.textContent = newLabel;
        }

        // Show/hide the dynamic checkout ("Buy with Shop") button to match availability.
        const container = button.closest('product-payment-container');
        const paymentButton = container && container.querySelector('.shopify-payment-button');
        if (paymentButton) {
          paymentButton.style.display = button.disabled ? 'none' : '';
        }
      });
    },
    true
  );
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * MW ADVANCED PRODUCT OPTIONS (APO) — HIDE BUY BUTTONS ON SOLD-OUT VARIANTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * APO renders its own "Add to cart" and "Buy now" buttons (it replaces the native dynamic-checkout button so
 * it can carry the add-on options). It hides those buttons correctly when a product loads on a sold-out
 * variant, but it does NOT re-evaluate availability when the shopper switches to a sold-out variant client
 * side — so the buttons wrongly stay visible. This is really APO's bug, but we patch it here.
 *
 * The theme's own "Sold out" button is handled by the sync above, and the "Notify me" button (the
 * "Notify! Back in Stock | PreOrder" app) already shows/hides correctly on its own, so we only need to
 * suppress APO's two buttons when the selected variant is unavailable.
 *
 * APO injects/re-renders its buttons asynchronously, so besides reacting to `variant:changed` we also watch
 * the DOM with a MutationObserver and re-apply the current state whenever APO re-renders its buttons.
 */
(function () {
  let isSoldOut = false;
  let observer = null;

  // APO injects two cloned buttons with these exact classes: the "Add to cart" clone and the "Buy now" clone.
  // They sit next to the theme's own #AddToCart button inside <product-payment-container>. We target only the
  // clones by class, so the theme's own Add to cart / Sold out button (handled by the sync above) is untouched.
  const APO_BUTTON_SELECTOR = '.mw-apo-add-to-cart-cloned, .mw-apo-buy-it-now-cloned';

  function getApoButtons() {
    return document.querySelectorAll(APO_BUTTON_SELECTOR);
  }

  function applyState() {
    getApoButtons().forEach((element) => {
      if (isSoldOut) {
        // APO's stylesheet sets `display: flex !important` on its buttons, so a plain inline style is ignored.
        // We must set the inline display with `!important` to win.
        if (element.style.display !== 'none' || element.style.getPropertyPriority('display') !== 'important') {
          element.style.setProperty('display', 'none', 'important');
        }
      } else if (element.style.display) {
        // Hand control back to APO's own stylesheet when the variant is available again.
        element.style.removeProperty('display');
      }
    });
  }

  function ensureObserver() {
    if (observer) {
      return;
    }
    // APO injects/re-renders its buttons asynchronously, so watch the DOM for node changes and re-apply the
    // current state. We only observe childList — our own style edits change attributes, not nodes, so they
    // won't retrigger this and cause a loop. applyState is cheap.
    observer = new MutationObserver(applyState);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('variant:changed', function (event) {
    const variant = event.detail && event.detail.variant;
    isSoldOut = !(variant && variant.available);
    ensureObserver();
    applyState();
  });
})();